import type { TextAsset } from '../TextAsset';
import type { WordElementMetrics } from '../metrics/WordElementMetrics';
import { Config } from '../../../../video-data/config';
import { TextDirection } from '../../../../enums';
import { int2rgb } from '../../../../libs/videoData';
import { getUpdatedHighlightRanges, sanitizeText } from './TextEditingUtils';

interface TextEditingState {
    text: string;
    selectionStart: number;
    selectionEnd: number;
}

/**
 * Canvas-based text editing manager.
 * Uses a hidden input for keyboard capture while rendering caret and selection on canvas.
 * This approach ensures correct positioning for RTL text since canvas handles RTL rendering correctly.
 */
export class CanvasTextEditingManager {
    textAsset: TextAsset = null!;
    readonly #domContainer: HTMLElement = null!;
    #nteContainer: HTMLDivElement | null = null;
    #hiddenInput: HTMLDivElement | null = null;
    #selectionStyleElement: HTMLStyleElement | null = null;
    #selectionColorDetector: HTMLDivElement | null = null;
    #domMutationObserver: MutationObserver | null = null;
    #ignoreDomMutations = false;
    #destroyed = false;
    #inOverflowState = false;
    #caretBlinkInterval: ReturnType<typeof setInterval> | null = null;
    #selectionStart = 0;
    #selectionEnd = 0;
    #editingEmptyState = false;

    // Invisible placeholder character used when editing empty text
    // This gives the TextAsset proper bounds for overflow checking
    static #EMPTY_STATE_PLACEHOLDER = 'I';

    // Undo/redo history
    #undoStack: TextEditingState[] = [];
    #redoStack: TextEditingState[] = [];
    static #MAX_HISTORY_SIZE = 100;

    static NTE_EVENT_EDITING_EXCEPTION = 'nte-editing-exception';
    static NTE_EVENT_TEXT_OVERFLOW = 'nte-text-overflow';
    static #isMac =
        typeof navigator !== 'undefined' && navigator.platform.toUpperCase().indexOf('MAC') >= 0;

    get #isRTL(): boolean {
        return this.textAsset.textDirection === TextDirection.RTL;
    }

    constructor(
        textAsset: TextAsset,
        domContainer: HTMLElement,
        initialClickEvent?: MouseEvent,
        initialClickPosition?: { x: number; y: number },
        outlineColor?: string,
    ) {
        if (!Config.isTextAsset(textAsset)) {
            throw new Error(
                'A valid TextAsset instance must be supplied when creating a CanvasTextEditingManager.',
            );
        }
        this.textAsset = textAsset;
        this.#domContainer = domContainer;

        this.#createHiddenInput();
        this.#createSelectionColorDetector();
        this.#setupDomMutationObserver();
        this.#setupEventListeners();
        this.#startCaretBlink();

        // Show empty state if text is empty (sets invisible placeholder for proper bounds)
        if (textAsset.text.length === 0) {
            this.#showEmptyState();
        }

        if (initialClickEvent) {
            this.#initClickTracking(initialClickEvent);
            this.#applyInitialClick(initialClickEvent);
        } else if (initialClickPosition && !this.#editingEmptyState) {
            const { x, y } = this.#convertSceneCoordsToMetricsCoords(
                initialClickPosition.x,
                initialClickPosition.y,
            );
            const charIndex = this.#getCharacterIndexAtPosition(x, y);
            this.#selectionStart = charIndex;
            this.#selectionEnd = charIndex;
        } else {
            this.#selectionStart = this.#editingEmptyState ? 0 : this.textAsset.text.length;
            this.#selectionEnd = this.#selectionStart;
        }
        this.#updateRendererSelection();

        if (outlineColor) {
            this.textAsset.renderer.setEditingOutlineColor(outlineColor);
            if (this.#hiddenInput) {
                this.#hiddenInput.style.outline = 'none';
            }
        }
    }

    get destroyed() {
        return this.#destroyed;
    }

    get domContainer(): HTMLElement {
        return this.#domContainer;
    }

    #initClickTracking(event?: MouseEvent) {
        if (!event) {
            return;
        }
        this.#clickCount = event.detail;
        this.#lastClickTime = Date.now();
        this.#lastClickX = event.clientX;
        this.#lastClickY = event.clientY;
    }

    #applyInitialClick(event?: MouseEvent) {
        if (!event || this.#editingEmptyState) {
            this.#selectionStart = this.#editingEmptyState ? 0 : this.textAsset.text.length;
            this.#selectionEnd = this.#selectionStart;
            return;
        }

        const charIndex = this.#getCharIndexFromMouseEvent(event);
        if (charIndex === null) {
            this.#selectionStart = this.textAsset.text.length;
            this.#selectionEnd = this.#selectionStart;
            return;
        }

        if (event.detail >= 3) {
            this.#selectionStart = this.#getLineStartIndex(charIndex);
            this.#selectionEnd = this.#getLineEndIndex(charIndex);
        } else if (event.detail === 2) {
            const wordBounds = this.#getWordBoundsAtIndex(this.textAsset.text, charIndex);
            this.#selectionStart = wordBounds.start;
            this.#selectionEnd = wordBounds.end;
        } else {
            this.#selectionStart = charIndex;
            this.#selectionEnd = charIndex;
        }
    }

    get inOverflowState() {
        return this.#inOverflowState;
    }

    set inOverflowState(isOverflowed: boolean) {
        const prevOverflowState = this.#inOverflowState;
        this.#inOverflowState = isOverflowed;

        if (isOverflowed && !prevOverflowState) {
            const webEvent = new Event(CanvasTextEditingManager.NTE_EVENT_TEXT_OVERFLOW);
            this.#domContainer.dispatchEvent(webEvent);
        }
    }

    get isShowingEmptyState(): boolean {
        return this.#editingEmptyState;
    }

    /**
     * Creates a single contenteditable element that serves as both the editing container
     * and the keyboard input. Positioned over the text asset bounds, it receives pointer
     * events directly so the browser's native context menu shows Copy/Cut/Paste.
     */
    #createHiddenInput() {
        // Replicate the old NTE DOM structure: #luminary-nte-container > #luminary-input
        // so the web layer (toolbar, overflow indicator, drag-and-drop) works without changes.
        this.#nteContainer = document.createElement('div');
        this.#nteContainer.id = 'luminary-nte-container';
        this.#nteContainer.style.position = 'absolute';

        this.#hiddenInput = document.createElement('div');
        this.#hiddenInput.id = 'luminary-input';
        this.#hiddenInput.contentEditable = 'true';
        this.#hiddenInput.setAttribute('translate', 'no');
        this.#hiddenInput.style.width = '100%';
        this.#hiddenInput.style.height = '100%';
        // Make text transparent - actual text is rendered on canvas
        this.#hiddenInput.style.color = 'transparent';
        this.#hiddenInput.style.caretColor = 'transparent';
        this.#hiddenInput.style.overflow = 'hidden';
        this.#hiddenInput.style.whiteSpace = 'pre-wrap';
        this.#hiddenInput.style.wordBreak = 'break-word';
        this.#hiddenInput.setAttribute('autocomplete', 'off');
        this.#hiddenInput.setAttribute('autocorrect', 'off');
        this.#hiddenInput.setAttribute('autocapitalize', 'off');
        this.#hiddenInput.setAttribute('spellcheck', 'false');
        // Required for web layer to recognize this as text editing input in handleBlur
        this.#hiddenInput.dataset.sceneeditingclass = 'text-input';

        // Initialize with actual text
        this.#hiddenInput.textContent = this.textAsset.text;

        // Suppress the native ::selection highlight — selection is drawn on canvas instead.
        this.#selectionStyleElement = document.createElement('style');
        this.#selectionStyleElement.textContent =
            '#luminary-input, #luminary-input * { -webkit-text-fill-color: transparent; } #luminary-input::selection, #luminary-input *::selection { background: transparent; color: transparent; -webkit-text-fill-color: transparent; }';
        document.head.appendChild(this.#selectionStyleElement);

        this.#nteContainer.appendChild(this.#hiddenInput);
        this.#domContainer.appendChild(this.#nteContainer);
        this.#updateRootContainerBounds();
        this.#hiddenInput.focus({ preventScroll: true });
    }

    /**
     * Updates the editing container position and size based on text asset bounds.
     */
    #updateRootContainerBounds() {
        if (!this.#nteContainer || !this.#hiddenInput || !this.textAsset.isLoaded) {
            return;
        }

        const bounds = this.textAsset.getNTEContainerBounds();
        const resolution = this.textAsset.root.referenceResolution;

        this.#nteContainer.style.left = `${(bounds.x / resolution.width) * 100}%`;
        this.#nteContainer.style.top = `${(bounds.y / resolution.height) * 100}%`;
        this.#nteContainer.style.width = `${(bounds.width / resolution.width) * 100}%`;
        this.#nteContainer.style.height = `${(bounds.height / resolution.height) * 100}%`;

        // Match font properties so DOM text layout approximates the canvas layout.
        // This ensures the DOM selection's visual bounds align with the canvas text,
        // which is required for the browser's native context menu to show "Copy".
        const containerRect = this.#domContainer.getBoundingClientRect();
        const domScale = containerRect.width / resolution.width;
        const style = this.textAsset.normalStyle;
        this.#hiddenInput.style.fontSize = `${style.fontSize * domScale}px`;
    }

    /**
     * Observes DOM mutations on the hidden input to detect external changes
     * (e.g., browser extensions modifying the contenteditable via Range API).
     * These mutations don't fire input events, so we need a MutationObserver.
     */
    #setupDomMutationObserver() {
        if (!this.#hiddenInput) return;

        this.#domMutationObserver = new MutationObserver(() => {
            if (this.#ignoreDomMutations) return;
            this.#applyDomTextToAsset();
        });

        this.#domMutationObserver.observe(this.#hiddenInput, {
            childList: true,
            characterData: true,
            subtree: true,
        });
    }

    /**
     * Creates a hidden element to detect the system/CSS selection color.
     * This element uses `background: Highlight` which gives us the actual selection color
     * that may be customized via CSS or system preferences.
     */
    #createSelectionColorDetector() {
        this.#selectionColorDetector = document.createElement('div');
        this.#selectionColorDetector.style.position = 'absolute';
        this.#selectionColorDetector.style.left = '-9999px';
        this.#selectionColorDetector.style.top = '0';
        this.#selectionColorDetector.style.width = '1px';
        this.#selectionColorDetector.style.height = '1px';
        this.#selectionColorDetector.style.pointerEvents = 'none';
        this.#selectionColorDetector.style.background = 'Highlight';
        this.#domContainer.appendChild(this.#selectionColorDetector);

        // Update renderer with detected selection color
        this.#updateSelectionColor();
    }

    /**
     * Reads the computed selection color from the detector element and updates the renderer.
     */
    #updateSelectionColor() {
        if (!this.#selectionColorDetector) return;

        const computedColor = getComputedStyle(this.#selectionColorDetector).backgroundColor;
        if (computedColor && computedColor !== 'rgba(0, 0, 0, 0)') {
            this.textAsset.renderer.setSelectionColor(computedColor);
        }
    }

    #setupEventListeners() {
        // Handle text input - use beforeinput to prevent default contenteditable behavior
        this.#hiddenInput.addEventListener('beforeinput', this.#handleBeforeInput);
        this.#hiddenInput.addEventListener('input', this.#handleInput);
        // Use capture phase for keydown to ensure we handle it before browser default behavior
        this.#hiddenInput.addEventListener('keydown', this.#handleKeydown, true);
        this.#hiddenInput.addEventListener('keyup', this.#handleKeyup, true);
        this.#hiddenInput.addEventListener('blur', this.#handleBlur);
        this.#hiddenInput.addEventListener('focus', this.#handleFocus);

        // Handle clipboard events on document so they work from context menus too
        document.addEventListener('copy', this.#handleCopy);
        document.addEventListener('cut', this.#handleCut);
        document.addEventListener('paste', this.#handlePaste);

        // Handle pointer events for cursor positioning and text selection
        // Use capture phase to intercept events before drag handlers
        // Pointer events are used because the drag system uses pointer events
        this.#domContainer.addEventListener('pointerdown', this.#handlePointerDown, true);
        this.#domContainer.addEventListener('pointermove', this.#handlePointerMove, true);
        this.#domContainer.addEventListener('pointerup', this.#handlePointerUp, true);
        this.#domContainer.addEventListener('dblclick', this.#handleDoubleClick, true);

        // Keep focus on hidden input
        this.#domContainer.addEventListener('click', this.#focusHiddenInput);

        // Prevent selection collapse on right-click, restore DOM selection for context menu
        this.#domContainer.addEventListener('mousedown', this.#handleMouseDownForContextMenu, true);
        this.#domContainer.addEventListener('contextmenu', this.#handleContextMenu);

        // Listen for clicks outside the text asset to blur the hidden input
        // This allows the web layer to handle the click and stop editing
        document.addEventListener('pointerdown', this.#handleDocumentPointerDown, true);
    }

    #removeEventListeners() {
        this.#hiddenInput.removeEventListener('beforeinput', this.#handleBeforeInput);
        this.#hiddenInput.removeEventListener('input', this.#handleInput);
        this.#hiddenInput.removeEventListener('keydown', this.#handleKeydown, true);
        this.#hiddenInput.removeEventListener('keyup', this.#handleKeyup, true);
        this.#hiddenInput.removeEventListener('blur', this.#handleBlur);
        this.#hiddenInput.removeEventListener('focus', this.#handleFocus);
        document.removeEventListener('copy', this.#handleCopy);
        document.removeEventListener('cut', this.#handleCut);
        document.removeEventListener('paste', this.#handlePaste);
        this.#domContainer.removeEventListener('pointerdown', this.#handlePointerDown, true);
        this.#domContainer.removeEventListener('pointermove', this.#handlePointerMove, true);
        this.#domContainer.removeEventListener('pointerup', this.#handlePointerUp, true);
        this.#domContainer.removeEventListener('dblclick', this.#handleDoubleClick, true);
        this.#domContainer.removeEventListener('click', this.#focusHiddenInput);
        this.#domContainer.removeEventListener(
            'mousedown',
            this.#handleMouseDownForContextMenu,
            true,
        );
        this.#domContainer.removeEventListener('contextmenu', this.#handleContextMenu);
        document.removeEventListener('pointerdown', this.#handleDocumentPointerDown, true);
    }

    #focusHiddenInput = () => {
        this.#hiddenInput?.focus({ preventScroll: true });
    };

    #handleMouseDownForContextMenu = (e: MouseEvent) => {
        if (e.button === 2 || (e.button === 0 && e.ctrlKey)) {
            e.preventDefault();
            e.stopPropagation();
            this.#hiddenInput?.focus({ preventScroll: true });
            this.#syncDomSelection();
        }
    };

    #handleContextMenu = () => {
        this.#hiddenInput?.focus({ preventScroll: true });
        this.#syncDomSelection();
    };

    /**
     * Handles pointer down events on the document.
     * If the click is outside the text asset and not on the toolbar,
     * blur the hidden input so the web layer can handle the click and stop editing.
     */
    #handleDocumentPointerDown = (e: PointerEvent) => {
        // Don't blur if clicking inside the text asset
        if (this.#isPointerInsideTextAsset(e)) {
            return;
        }

        // Don't blur if clicking on the toolbar - this allows toolbar buttons to work
        const toolbar = document.querySelector('.scene-editing-toolbar');
        if (toolbar && toolbar.contains(e.target as Node)) {
            return;
        }

        this.#hiddenInput?.blur();
    };

    #handleBlur = (e: FocusEvent) => {
        // Check if focus is moving to the toolbar - if so, don't clear selection
        const relatedTarget = e.relatedTarget as HTMLElement | null;
        const toolbar = document.querySelector('.scene-editing-toolbar');
        if (toolbar && relatedTarget && toolbar.contains(relatedTarget)) {
            // Focus is moving to toolbar, keep selection visible but stop caret blink
            this.#stopCaretBlink();
            return;
        }

        // Hide caret when input loses focus
        this.#stopCaretBlink();
        this.textAsset.renderer.setEditingSelection(null, null);
        this.textAsset.requiresRedraw = true;
    };

    #handleFocus = () => {
        // Show caret when input gains focus
        this.#updateRendererSelection();
        this.#startCaretBlink();
    };

    #handleCopy = (e: ClipboardEvent) => {
        if (document.activeElement !== this.#hiddenInput) return;

        const selStart = Math.min(this.#selectionStart, this.#selectionEnd);
        const selEnd = Math.max(this.#selectionStart, this.#selectionEnd);

        if (selStart === selEnd) {
            return;
        }

        const selectedText = this.textAsset.text.slice(selStart, selEnd);
        e.clipboardData?.setData('text/plain', selectedText);
        e.preventDefault();
    };

    #handleCut = (e: ClipboardEvent) => {
        if (document.activeElement !== this.#hiddenInput) return;

        const selStart = Math.min(this.#selectionStart, this.#selectionEnd);
        const selEnd = Math.max(this.#selectionStart, this.#selectionEnd);

        if (selStart === selEnd) {
            return;
        }

        const selectedText = this.textAsset.text.slice(selStart, selEnd);
        e.clipboardData?.setData('text/plain', selectedText);
        e.preventDefault();

        // Delete the selected text
        this.#saveState();
        this.#deleteSelection();
    };

    #handlePaste = (e: ClipboardEvent) => {
        if (document.activeElement !== this.#hiddenInput) return;

        e.preventDefault();

        const pastedText = e.clipboardData?.getData('text/plain');
        if (!pastedText) {
            return;
        }

        const sanitizedText = sanitizeText(pastedText);
        if (sanitizedText.length === 0) {
            return;
        }

        const selStart = Math.min(this.#selectionStart, this.#selectionEnd);
        const selEnd = Math.max(this.#selectionStart, this.#selectionEnd);

        // In empty state, the actual text is '' (the 'I' is just a placeholder for bounds)
        const originalText = this.#editingEmptyState ? '' : this.textAsset.text;
        const beforeSelection = originalText.slice(0, selStart);
        const afterSelection = originalText.slice(selEnd);
        const updatedText = beforeSelection + sanitizedText + afterSelection;

        const { canFit, newTextSize } = this.handleOverflowState(updatedText);
        if (!canFit) {
            return;
        }

        // Remove empty state before making changes
        if (this.#editingEmptyState) {
            this.#removeEmptyState();
        }

        this.#saveState();
        this.inOverflowState = false;

        const deletedLength = selEnd - selStart;
        const netCharDiff = sanitizedText.length - deletedLength;

        // Update text before ranges to ensure _checkAndFixProperties sees the new text length
        this.textAsset.text = updatedText;
        if (newTextSize !== null) {
            this.textAsset.textSize = newTextSize;
        }
        this.#updateRangesOnTextChange(netCharDiff, selStart);

        const newCursorPos = selStart + sanitizedText.length;
        this.#selectionStart = newCursorPos;
        this.#selectionEnd = newCursorPos;

        this.#updateRendererSelection();
        this.#resetCaretBlink();
        this.textAsset.requiresRedraw = true;
    };

    #saveState() {
        const state: TextEditingState = {
            text: this.textAsset.text,
            selectionStart: this.#selectionStart,
            selectionEnd: this.#selectionEnd,
        };

        this.#undoStack.push(state);
        if (this.#undoStack.length > CanvasTextEditingManager.#MAX_HISTORY_SIZE) {
            this.#undoStack.shift();
        }

        // Clear redo stack when a new action is performed
        this.#redoStack = [];
    }

    #undo() {
        if (this.#undoStack.length === 0) {
            return;
        }

        // Save current state to redo stack
        const currentState: TextEditingState = {
            text: this.textAsset.text,
            selectionStart: this.#selectionStart,
            selectionEnd: this.#selectionEnd,
        };
        this.#redoStack.push(currentState);

        // Restore previous state
        const previousState = this.#undoStack.pop()!;
        this.textAsset.text = previousState.text;
        this.#selectionStart = previousState.selectionStart;
        this.#selectionEnd = previousState.selectionEnd;

        this.#updateRendererSelection();
        this.#resetCaretBlink();
        this.textAsset.requiresRedraw = true;
    }

    #redo() {
        if (this.#redoStack.length === 0) {
            return;
        }

        // Save current state to undo stack
        const currentState: TextEditingState = {
            text: this.textAsset.text,
            selectionStart: this.#selectionStart,
            selectionEnd: this.#selectionEnd,
        };
        this.#undoStack.push(currentState);

        // Restore next state
        const nextState = this.#redoStack.pop()!;
        this.textAsset.text = nextState.text;
        this.#selectionStart = nextState.selectionStart;
        this.#selectionEnd = nextState.selectionEnd;

        this.#updateRendererSelection();
        this.#resetCaretBlink();
        this.textAsset.requiresRedraw = true;
    }

    /**
     * Shows the empty state with an invisible placeholder character.
     * Sets textAsset.text to 'I' for proper bounds calculation,
     * but hides the text content so the placeholder is not visible.
     */
    #showEmptyState() {
        this.#editingEmptyState = true;

        // Clear any highlights when entering empty state
        if (this.textAsset.textHighlights.length) {
            this.textAsset.textHighlights = [];
        }

        // Set placeholder text for proper bounds, but make it invisible
        this.textAsset.text = CanvasTextEditingManager.#EMPTY_STATE_PLACEHOLDER;
        this.textAsset.renderer.setHideTextContent(true);
    }

    /**
     * Removes the empty state when user starts typing.
     * The caller is responsible for setting the new text before calling this.
     */
    #removeEmptyState() {
        if (!this.#editingEmptyState) {
            return;
        }

        this.#editingEmptyState = false;
        this.textAsset.renderer.setHideTextContent(false);
    }

    #isPointerDown = false;
    #lastClickTime = 0;
    #lastClickX = 0;
    #lastClickY = 0;
    #clickCount = 0;
    static #TRIPLE_CLICK_THRESHOLD = 500; // ms
    static #CLICK_POSITION_THRESHOLD = 10; // pixels - max distance between clicks to count as multi-click

    /**
     * Checks if a pointer event is within the text asset's bounds.
     * Converts the text asset's pixi bounds to viewport coordinates.
     */
    #isPointerInsideTextAsset(e: PointerEvent): boolean {
        const containerRect = this.#domContainer.getBoundingClientRect();
        const pixiBounds = this.textAsset._pixiContainer.getBounds();

        // Convert pixi bounds to viewport coordinates
        // The pixi bounds are in reference resolution (see BaseTextAsset._calculateBounds)
        const root = this.textAsset.root;
        const refWidth = root.referenceResolution.width;
        const refHeight = root.referenceResolution.height;

        const domScaleX = containerRect.width / refWidth;
        const domScaleY = containerRect.height / refHeight;

        const assetLeft = containerRect.left + pixiBounds.x * domScaleX;
        const assetTop = containerRect.top + pixiBounds.y * domScaleY;
        const assetRight = assetLeft + pixiBounds.width * domScaleX;
        const assetBottom = assetTop + pixiBounds.height * domScaleY;

        const x = e.clientX;
        const y = e.clientY;

        return x >= assetLeft && x <= assetRight && y >= assetTop && y <= assetBottom;
    }

    #handlePointerDown = (e: PointerEvent) => {
        // Only intercept events inside the text asset
        if (!this.#isPointerInsideTextAsset(e)) {
            return;
        }

        // Right-click or Ctrl+click (macOS trackpad): preserve selection, allow context menu.
        // Only stopPropagation to prevent drag — don't preventDefault so mousedown/contextmenu
        // still fire. The mousedown handler will preventDefault to prevent selection collapse.
        if (e.button === 2 || (e.button === 0 && e.ctrlKey)) {
            e.stopPropagation();
            return;
        }

        // Prevent asset dragging while in text editing mode
        e.stopPropagation();
        e.preventDefault();

        // Track click count for triple-click detection
        // Only count as multi-click if within time threshold AND at similar position
        const now = Date.now();
        const dx = Math.abs(e.clientX - this.#lastClickX);
        const dy = Math.abs(e.clientY - this.#lastClickY);
        const timeSinceLastClick = now - this.#lastClickTime;
        const isNearLastClick =
            dx < CanvasTextEditingManager.#CLICK_POSITION_THRESHOLD &&
            dy < CanvasTextEditingManager.#CLICK_POSITION_THRESHOLD;

        if (
            timeSinceLastClick < CanvasTextEditingManager.#TRIPLE_CLICK_THRESHOLD &&
            isNearLastClick
        ) {
            this.#clickCount++;
        } else {
            this.#clickCount = 1;
        }

        this.#lastClickTime = now;
        this.#lastClickX = e.clientX;
        this.#lastClickY = e.clientY;

        this.#isPointerDown = true;
        const charIndex = this.#getCharIndexFromPointerEvent(e);
        if (charIndex !== null) {
            if (this.#clickCount >= 3) {
                // Triple-click: select entire line
                const lineStart = this.#getLineStartIndex(charIndex);
                const lineEnd = this.#getLineEndIndex(charIndex);
                this.#selectionStart = lineStart;
                this.#selectionEnd = lineEnd;
            } else if (e.shiftKey) {
                // Shift+Click: extend selection to click position
                this.#selectionEnd = charIndex;
            } else {
                // Regular click: set caret position
                this.#selectionStart = charIndex;
                this.#selectionEnd = charIndex;
            }
            this.#updateRendererSelection();
            this.#resetCaretBlink();
        }
        this.#focusHiddenInput();
    };

    #handlePointerMove = (e: PointerEvent) => {
        if (!this.#isPointerDown) return;

        // Prevent asset dragging while selecting text
        e.stopPropagation();
        e.preventDefault();

        const charIndex = this.#getCharIndexFromPointerEvent(e);
        if (charIndex !== null) {
            this.#selectionEnd = charIndex;
            this.#updateRendererSelection();
        }
    };

    #handlePointerUp = (e: PointerEvent) => {
        if (this.#isPointerDown) {
            e.stopPropagation();
            this.#syncDomSelection();
            document.dispatchEvent(new Event('selectionchange'));
        }
        this.#isPointerDown = false;
    };

    #handleDoubleClick = (e: MouseEvent) => {
        if (!this.#isPointerInsideTextAsset(e as PointerEvent)) {
            return;
        }

        e.stopPropagation();
        e.preventDefault();

        const charIndex = this.#getCharIndexFromMouseEvent(e);
        if (charIndex === null) {
            return;
        }

        const text = this.textAsset.text;
        const wordBounds = this.#getWordBoundsAtIndex(text, charIndex);

        this.#selectionStart = wordBounds.start;
        this.#selectionEnd = wordBounds.end;
        this.#updateRendererSelection();
        this.#resetCaretBlink();
        this.#focusHiddenInput();
    };

    #getWordBoundsAtIndex(text: string, index: number): { start: number; end: number } {
        if (text.length === 0) {
            return { start: 0, end: 0 };
        }

        // Clamp index to valid range
        index = Math.max(0, Math.min(index, text.length - 1));

        // Check if we're on a word character (includes Unicode letters and digits)
        // \p{L} matches any Unicode letter (including accented chars like ö, é, etc.)
        // \p{N} matches any Unicode digit
        const isWordChar = (char: string) => /[\p{L}\p{N}_]/u.test(char);

        // If clicked on whitespace/punctuation, just return that character
        if (!isWordChar(text[index])) {
            return { start: index, end: index + 1 };
        }

        // Find word start
        let start = index;
        while (start > 0 && isWordChar(text[start - 1])) {
            start--;
        }

        // Find word end
        let end = index;
        while (end < text.length && isWordChar(text[end])) {
            end++;
        }

        return { start, end };
    }

    /**
     * Gets the index of the start of the visual line containing the given position.
     * Uses text metrics to account for word wrapping.
     * Used for Cmd+Left, Cmd+Backspace, Home key.
     */
    #getLineStartIndex(index: number): number {
        const metrics = this.textAsset.renderer.metrics;
        if (!metrics || metrics.lines.length === 0) {
            return this.#getParagraphStartIndex(index);
        }

        // Find which visual line contains the given index.
        // For non-last lines, use exclusive upper bound (index < lineEnd) so that
        // a position at the boundary between two lines is assigned to the next line.
        // This ensures Cmd+Left / Ctrl+A from a wrap boundary stays on the correct line.
        let charIndex = 0;
        for (const [idx, line] of metrics.lines.entries()) {
            const lineStart = charIndex;
            const lineEnd = charIndex + line.text.length;
            const isLastLine = idx === metrics.lines.length - 1;

            const withinLineEnd = isLastLine ? index <= lineEnd : index < lineEnd;
            if (index >= lineStart && withinLineEnd) {
                return lineStart;
            }
            charIndex = lineEnd;
        }

        // Fallback to last line start
        let lastLineStart = 0;
        for (let i = 0; i < metrics.lines.length - 1; i++) {
            lastLineStart += metrics.lines[i].text.length;
        }
        return lastLineStart;
    }

    /**
     * Gets the index of the end of the visual line containing the given position.
     * Uses text metrics to account for word wrapping.
     * Used for Cmd+Right, Cmd+Delete, End key.
     */
    #getLineEndIndex(index: number): number {
        const metrics = this.textAsset.renderer.metrics;
        if (!metrics || metrics.lines.length === 0) {
            return this.#getParagraphEndIndex(index);
        }

        // Find which visual line contains the given index.
        // For non-last lines, use exclusive upper bound (index < lineEnd) so that
        // a position at the boundary between two lines is assigned to the next line.
        // This ensures Cmd+Right / Ctrl+E from a wrap boundary moves to the correct line end.
        let charIndex = 0;
        for (const [idx, line] of metrics.lines.entries()) {
            const lineStart = charIndex;
            const lineEnd = charIndex + line.text.length;
            const isLastLine = idx === metrics.lines.length - 1;

            const withinLineEnd = isLastLine ? index <= lineEnd : index < lineEnd;
            if (index >= lineStart && withinLineEnd) {
                // Don't include the trailing newline character in the "end" position
                // This matches standard text editor behavior
                const textWithoutNewline = line.text.replace(/\n$/, '');
                return lineStart + textWithoutNewline.length;
            }
            charIndex = lineEnd;
        }

        // Fallback to end of text
        return this.textAsset.text.length;
    }

    /**
     * Gets the index of the start of the paragraph (logical line) containing the given position.
     * Uses '\n' as the line separator.
     * Used for Ctrl+A (macOS Emacs-style).
     */
    #getParagraphStartIndex(index: number): number {
        const text = this.textAsset.text;
        let pos = index - 1;
        while (pos >= 0 && text[pos] !== '\n') {
            pos--;
        }
        return pos + 1;
    }

    /**
     * Gets the index of the end of the paragraph (logical line) containing the given position.
     * Uses '\n' as the line separator.
     * Used for Ctrl+E (macOS Emacs-style).
     */
    #getParagraphEndIndex(index: number): number {
        const text = this.textAsset.text;
        let pos = index;
        while (pos < text.length && text[pos] !== '\n') {
            pos++;
        }
        return pos;
    }

    /**
     * Gets the index of the start of the word before the given position.
     * Skips whitespace first, then finds the start of the word.
     */
    #getWordStartIndex(index: number): number {
        const text = this.textAsset.text;
        let pos = index - 1;

        // Skip whitespace
        while (pos >= 0 && /\s/.test(text[pos])) {
            pos--;
        }

        // Skip word characters (Unicode letters, digits, underscore)
        while (pos >= 0 && /[\p{L}\p{N}_]/u.test(text[pos])) {
            pos--;
        }

        return pos + 1;
    }

    /**
     * Gets the index of the end of the word after the given position.
     * Skips whitespace first, then finds the end of the word.
     */
    #getWordEndIndex(index: number): number {
        const text = this.textAsset.text;
        let pos = index;

        // Skip whitespace
        while (pos < text.length && /\s/.test(text[pos])) {
            pos++;
        }

        // Skip word characters (Unicode letters, digits, underscore)
        while (pos < text.length && /[\p{L}\p{N}_]/u.test(text[pos])) {
            pos++;
        }

        return pos;
    }

    #getCharIndexFromMouseEvent(e: MouseEvent): number | null {
        const rect = this.#domContainer.getBoundingClientRect();
        const root = this.textAsset.root;
        const outputWidth = root.outputResolution.width;
        const outputHeight = root.outputResolution.height;

        const scaleX = outputWidth / rect.width;
        const scaleY = outputHeight / rect.height;

        const canvasX = (e.clientX - rect.left) * scaleX;
        const canvasY = (e.clientY - rect.top) * scaleY;

        return this.#getCharacterIndexAtPosition(canvasX, canvasY);
    }

    #getCharIndexFromPointerEvent(e: PointerEvent): number | null {
        const rect = this.#domContainer.getBoundingClientRect();
        const root = this.textAsset.root;
        const outputWidth = root.outputResolution.width;
        const outputHeight = root.outputResolution.height;

        // Convert mouse coordinates from DOM to output resolution coordinates
        const scaleX = outputWidth / rect.width;
        const scaleY = outputHeight / rect.height;

        const canvasX = (e.clientX - rect.left) * scaleX;
        const canvasY = (e.clientY - rect.top) * scaleY;

        return this.#getCharacterIndexAtPosition(canvasX, canvasY);
    }

    /**
     * Calculates which character index corresponds to a given canvas position.
     * Input coordinates are in canvas/output resolution (absolute).
     * Line bounds are in local metrics coordinates.
     */
    #getCharacterIndexAtPosition(canvasX: number, canvasY: number): number {
        const metrics = this.textAsset.renderer.metrics;
        if (!metrics || metrics.lines.length === 0) {
            return 0;
        }

        // Convert from canvas coords to local metrics coords
        // The renderer applies transformations in _applyActiveBoundsTransformation:
        // 1. translate(canvasAnchorX, canvasAnchorY) where canvasAnchorX = -(anchorX * activeTextBounds.width)
        // 2. translate(translateX, translateY)
        // 3. scale(scaleX, scaleY)
        // 4. translate(-activeTextBounds.x, -activeTextBounds.y)
        //
        // To reverse: we need to undo these in reverse order
        const renderer = this.textAsset.renderer;
        const activeTextBounds = renderer.activeTextBounds;
        const anchorOffsetX = renderer.anchorX * activeTextBounds.width;
        const anchorOffsetY = renderer.anchorY * activeTextBounds.height;

        // Reverse the transformation chain:
        // canvasX = (localX - activeTextBounds.x) * scaleX + translateX - anchorOffsetX
        // So: localX = (canvasX - translateX + anchorOffsetX) / scaleX + activeTextBounds.x
        const x =
            (canvasX - renderer.translateX + anchorOffsetX) / renderer.scaleX + activeTextBounds.x;
        const y =
            (canvasY - renderer.translateY + anchorOffsetY) / renderer.scaleY + activeTextBounds.y;

        let charIndex = 0;

        for (const line of metrics.lines) {
            const lineBounds = line.bounds;

            // Check if y is within this line
            if (y >= lineBounds.y && y <= lineBounds.y + lineBounds.height) {
                // Find the closest character position on this line
                let lineCharIndex = charIndex;
                let closestCharIndex = charIndex;
                let closestDistance = Infinity;

                for (const word of line.words) {
                    for (const element of word.elements) {
                        // Use advance-width bounds for hit testing to ensure contiguous
                        // coverage with no gaps between elements (visible pixel bounds
                        // from element.bounds can be narrower than advance width)
                        const elemStartX = element.x;
                        const elemEndX = element.x + element.advanceWidth;

                        // Check if x is within this element's advance box
                        if (element.advanceWidth > 0 && x >= elemStartX && x <= elemEndX) {
                            const relativeX = x - element.x;
                            const charOffset = this.textAsset.renderer.getCharIndexInElement(
                                element,
                                relativeX,
                            );
                            return lineCharIndex + charOffset;
                        }

                        // Track closest position for when click is between elements
                        // (e.g., hidden trailing spaces with zero advance width)
                        const distToStart = Math.abs(x - elemStartX);
                        if (distToStart < closestDistance) {
                            closestDistance = distToStart;
                            closestCharIndex = lineCharIndex;
                        }

                        const distToEnd = Math.abs(x - elemEndX);
                        if (distToEnd < closestDistance) {
                            closestDistance = distToEnd;
                            closestCharIndex = lineCharIndex + element.text.length;
                        }

                        lineCharIndex += element.text.length;
                    }
                }

                // Return the closest character position on this line
                return closestCharIndex;
            }

            // If we're above this line, return start
            if (y < lineBounds.y) {
                return charIndex;
            }

            charIndex += line.text.length;
        }

        // Click is below all lines - return end of text
        return this.textAsset.text.length;
    }

    /**
     * Gets the caret position on an adjacent line (for arrow up/down navigation).
     * Uses accurate element-based X position calculation consistent with click handling:
     * 1. Find current X position using element bounds
     * 2. Find the element on target line that contains this X position
     * 3. Use accurate character measurement within that element
     * @param direction -1 for up, 1 for down
     * @returns The new character index
     */
    #getCaretPositionOnAdjacentLine(direction: -1 | 1): number {
        const metrics = this.textAsset.renderer.metrics;
        if (!metrics || metrics.lines.length === 0) {
            return this.#selectionEnd;
        }

        const currentPos = this.#selectionEnd;

        // Step 1: Find current line and calculate current X position using element bounds
        let lineStartCharIndex = 0;
        let currentLineIndex = -1;
        let currentX = 0;

        for (let lineIdx = 0; lineIdx < metrics.lines.length; lineIdx++) {
            const line = metrics.lines[lineIdx];
            const lineEnd = lineStartCharIndex + line.text.length;
            const isLastLine = lineIdx === metrics.lines.length - 1;

            // For non-last lines, use exclusive upper bound so a position at the
            // boundary between two lines is assigned to the next line.
            const withinLineEnd = isLastLine ? currentPos <= lineEnd : currentPos < lineEnd;
            if (currentPos >= lineStartCharIndex && withinLineEnd) {
                currentLineIndex = lineIdx;
                const charInLine = currentPos - lineStartCharIndex;

                // Find which element contains this character position and calculate X
                // Uses the renderer's accurate character measurement for correct RTL positioning
                let elemCharStart = 0;
                let foundElement = false;

                for (const word of line.words) {
                    for (const element of word.elements) {
                        const elemCharEnd = elemCharStart + element.text.length;
                        if (charInLine >= elemCharStart && charInLine <= elemCharEnd) {
                            const charOffsetInElement = charInLine - elemCharStart;
                            currentX = this.textAsset.renderer._getElementCharX(
                                element,
                                charOffsetInElement,
                            );
                            foundElement = true;
                            break;
                        }
                        elemCharStart = elemCharEnd;
                    }
                    if (foundElement) break;
                }

                // If cursor is after all elements, use end of last element
                if (!foundElement && line.words.length > 0) {
                    const lastWord = line.words[line.words.length - 1];
                    if (lastWord.elements.length > 0) {
                        const lastElement = lastWord.elements[lastWord.elements.length - 1];
                        currentX = this.textAsset.renderer._getElementCharX(
                            lastElement,
                            lastElement.text.length,
                        );
                    }
                }
                break;
            }
            lineStartCharIndex = lineEnd;
        }

        if (currentLineIndex === -1) {
            return this.#selectionEnd;
        }

        // Step 2: Calculate target line index and handle boundaries
        const targetLineIndex = currentLineIndex + direction;

        if (targetLineIndex < 0) {
            return 0;
        }
        if (targetLineIndex >= metrics.lines.length) {
            return this.textAsset.text.length;
        }

        // Step 3: Calculate start character index of target line
        let targetLineStart = 0;
        for (let i = 0; i < targetLineIndex; i++) {
            targetLineStart += metrics.lines[i].text.length;
        }

        const targetLine = metrics.lines[targetLineIndex];

        if (targetLine.words.length === 0) {
            return targetLineStart;
        }

        // Step 4: Find the position on target line that matches currentX
        // Collect all visible elements (skip newlines and zero-width elements)
        interface ElementInfo {
            element: WordElementMetrics;
            lineCharIndex: number;
            startX: number;
            endX: number;
        }

        const visibleElements: ElementInfo[] = [];
        let lineCharIndex = targetLineStart;

        for (const word of targetLine.words) {
            for (const element of word.elements) {
                // Skip newlines and zero-width elements - we want visible content only
                const isNewline = element.text === '\n' || element.text === '\r\n';
                if (!isNewline && element.advanceWidth > 0) {
                    visibleElements.push({
                        element,
                        lineCharIndex,
                        startX: element.x,
                        endX: element.x + element.advanceWidth,
                    });
                }
                lineCharIndex += element.text.length;
            }
        }

        if (visibleElements.length === 0) {
            return targetLineStart;
        }

        // Find the line's actual visible bounds (min/max X across all elements)
        let lineMinX = Infinity;
        let lineMaxX = -Infinity;
        for (const elem of visibleElements) {
            lineMinX = Math.min(lineMinX, elem.startX);
            lineMaxX = Math.max(lineMaxX, elem.endX);
        }

        const lastVisibleElement = visibleElements[visibleElements.length - 1];
        const lineEndCharIndex =
            lastVisibleElement.lineCharIndex + lastVisibleElement.element.text.length;

        // Handle edge cases: currentX is outside the visible line bounds
        if (currentX <= lineMinX) {
            return this.#isRTL ? lineEndCharIndex : targetLineStart;
        }

        if (currentX >= lineMaxX) {
            return this.#isRTL ? targetLineStart : lineEndCharIndex;
        }

        // Find the element that contains currentX
        for (const elemInfo of visibleElements) {
            if (currentX >= elemInfo.startX && currentX <= elemInfo.endX) {
                // Use accurate character measurement within this element
                const relativeX = currentX - elemInfo.element.x;
                const charOffset = this.textAsset.renderer.getCharIndexInElement(
                    elemInfo.element,
                    relativeX,
                );
                return elemInfo.lineCharIndex + charOffset;
            }
        }

        // currentX is between elements - find the closest position
        // For LTR: left edge = element start, right edge = element end
        // For RTL: left edge = element end, right edge = element start
        let closestCharIndex = targetLineStart;
        let closestDistance = Infinity;

        for (const elemInfo of visibleElements) {
            const startCharIndex = this.#isRTL
                ? elemInfo.lineCharIndex + elemInfo.element.text.length
                : elemInfo.lineCharIndex;
            const endCharIndex = this.#isRTL
                ? elemInfo.lineCharIndex
                : elemInfo.lineCharIndex + elemInfo.element.text.length;

            const distToStart = Math.abs(currentX - elemInfo.startX);
            if (distToStart < closestDistance) {
                closestDistance = distToStart;
                closestCharIndex = startCharIndex;
            }

            const distToEnd = Math.abs(currentX - elemInfo.endX);
            if (distToEnd < closestDistance) {
                closestDistance = distToEnd;
                closestCharIndex = endCharIndex;
            }
        }

        return closestCharIndex;
    }

    /**
     * Handles beforeinput events.
     * We allow most input types to go through natively, only preventing paste
     * (handled separately) to sanitize the text.
     */
    #handleBeforeInput = (e: InputEvent) => {
        // Only prevent paste - we handle it separately to sanitize text
        if (e.inputType === 'insertFromPaste') {
            e.preventDefault();
        }
    };

    /**
     * Handles input events for IME and other input methods that bypass keydown.
     * The keydown handler handles most input, but input events are needed for:
     * - IME composition (Chinese, Japanese, Korean input)
     * - macOS accent popup (hold key to select accented character)
     * - Programmatic input via dispatchEvent (tests)
     */
    #handleInput = (e: Event) => {
        const inputEvent = e as InputEvent;

        // Handle text insertion events
        const isTextInsertion =
            inputEvent.inputType === 'insertText' ||
            inputEvent.inputType === 'insertReplacementText' ||
            inputEvent.inputType === 'insertCompositionText';

        if (isTextInsertion && inputEvent.data) {
            // Check if keydown already inserted this exact character
            if (this.#lastKeydownInsertedChar === inputEvent.data) {
                // Keydown already handled this, skip to avoid double insertion.
                // Set flag to indicate we're waiting for potential accent popup selection.
                // This is the browser confirming our keydown - user might be holding for accent popup.
                this.#waitingForAccentPopupSelection = true;
                return;
            }

            // Handle replacement (e.g., macOS accent popup selecting a different character)
            // The keydown inserted the original char, now we need to replace it with the new one
            if (this.#lastKeydownInsertedChar) {
                // Move cursor back to before the character that keydown inserted
                const pos = this.#selectionEnd;
                if (pos > 0 && !this.#editingEmptyState) {
                    this.#selectionStart = pos - 1;
                    this.#selectionEnd = pos; // Select the character to be replaced
                }
                this.#lastKeydownInsertedChar = null;
                this.#lastKeydownKey = null;
                this.#waitingForAccentPopupSelection = false;
                this.#insertText(inputEvent.data);
                return;
            }

            // No preceding keydown — insertText from browser, execCommand, or extension
            this.#insertText(inputEvent.data);
            return;
        }

        // External mutation: browser extensions that set textContent and dispatch generic input events.
        // Read the text directly from the contenteditable DOM.
        this.#lastKeydownInsertedChar = null;
        this.#lastKeydownKey = null;
        this.#waitingForAccentPopupSelection = false;
        this.#applyDomTextToAsset();
    };

    #handleKeyup = (e: KeyboardEvent) => {
        // Clear state when the key is released.
        // This helps distinguish between:
        // - Normal typing: keydown 'o', keyup 'o', keydown '2' (should type '2')
        // - Accent popup: keydown 'o', (hold), keydown '2' (should select from popup, not type '2')
        if (this.#lastKeydownKey === e.key) {
            this.#lastKeydownKey = null;
            // Also clear accent popup state - if the user released the key,
            // they're done with the potential accent popup
            this.#lastKeydownInsertedChar = null;
            this.#waitingForAccentPopupSelection = false;
        }
    };

    /**
     * Reads the current text from the contenteditable DOM and applies it to the text asset.
     * Used when external code (browser extensions, execCommand) modifies the DOM directly.
     */
    #applyDomTextToAsset() {
        if (!this.#hiddenInput) return;

        const domText = sanitizeText(this.#hiddenInput.textContent || '');
        const currentText = this.#editingEmptyState ? '' : this.textAsset.text;

        if (domText === currentText) return;

        if (domText.trim().length === 0) {
            this.#saveState();
            this.#showEmptyState();
            this.#selectionStart = 0;
            this.#selectionEnd = 0;
            this.#updateRendererSelection();
            this.#resetCaretBlink();
            this.textAsset.requiresRedraw = true;
            return;
        }

        if (this.#editingEmptyState) {
            this.#removeEmptyState();
        }

        this.#saveState();
        this.textAsset.text = domText;
        // Sync cursor position from DOM after external mutation
        this.#syncSelectionFromContentEditable();
        this.#updateRendererSelection();
        this.#resetCaretBlink();
        this.textAsset.requiresRedraw = true;
    }

    #syncSelectionFromContentEditable() {
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) {
            return;
        }
        const range = selection.getRangeAt(0);
        if (!this.#hiddenInput.contains(range.startContainer)) {
            return;
        }

        // Get the text offset in the contenteditable
        const preCaretRange = range.cloneRange();
        preCaretRange.selectNodeContents(this.#hiddenInput);
        preCaretRange.setEnd(range.startContainer, range.startOffset);
        const start = preCaretRange.toString().length;

        preCaretRange.setEnd(range.endContainer, range.endOffset);
        const end = preCaretRange.toString().length;

        this.#selectionStart = start;
        this.#selectionEnd = end;
    }

    #syncSelectionToContentEditable() {
        const selection = window.getSelection();
        if (!selection) {
            return;
        }

        const textNode = this.#hiddenInput.firstChild;
        if (!textNode) {
            return;
        }

        try {
            const range = document.createRange();
            const start = Math.min(this.#selectionStart, textNode.textContent?.length || 0);
            const end = Math.min(this.#selectionEnd, textNode.textContent?.length || 0);
            range.setStart(textNode, start);
            range.setEnd(textNode, end);
            selection.removeAllRanges();
            selection.addRange(range);
        } catch (e) {
            // Ignore selection errors
        }
    }

    #handleKeydown = (e: KeyboardEvent) => {
        // Skip handling during IME composition
        if (e.isComposing) {
            return;
        }

        const selStart = Math.min(this.#selectionStart, this.#selectionEnd);
        const selEnd = Math.max(this.#selectionStart, this.#selectionEnd);
        const hasSelection = selStart !== selEnd;

        switch (e.key) {
            case 'Backspace':
                e.preventDefault();
                if (hasSelection) {
                    this.#saveState();
                    this.#deleteSelection();
                } else if (selStart > 0 && !this.#editingEmptyState) {
                    this.#saveState();
                    let deleteToIndex: number;

                    if (e.metaKey) {
                        // Cmd+Backspace (Mac): delete to start of line
                        deleteToIndex = this.#getLineStartIndex(selStart);
                    } else if (e.altKey || e.ctrlKey) {
                        // Option+Backspace (Mac) or Ctrl+Backspace (Windows): delete word left
                        deleteToIndex = this.#getWordStartIndex(selStart);
                    } else {
                        // Regular backspace: delete one character
                        deleteToIndex = selStart - 1;
                    }

                    const deletedLength = selStart - deleteToIndex;
                    this.#updateRangesOnTextChange(-deletedLength, deleteToIndex);

                    const before = this.textAsset.text.slice(0, deleteToIndex);
                    const after = this.textAsset.text.slice(selStart);
                    const newText = before + after;

                    // If text becomes empty, show empty state
                    if (newText.trim().length === 0) {
                        this.#showEmptyState();
                        this.#selectionStart = 0;
                        this.#selectionEnd = 0;
                    } else {
                        this.textAsset.text = newText;
                        this.#selectionStart = deleteToIndex;
                        this.#selectionEnd = deleteToIndex;
                    }

                    this.#updateRendererSelection();
                    this.textAsset.requiresRedraw = true;
                    // Deleting text can never cause overflow
                    this.inOverflowState = false;
                }
                this.#resetCaretBlink();
                break;

            case 'Delete':
                e.preventDefault();
                if (hasSelection) {
                    this.#saveState();
                    this.#deleteSelection();
                } else if (selStart < this.textAsset.text.length && !this.#editingEmptyState) {
                    this.#saveState();
                    let deleteToIndex: number;

                    if (e.metaKey) {
                        // Cmd+Delete (Mac): delete to end of line
                        deleteToIndex = this.#getLineEndIndex(selStart);
                    } else if (e.altKey || e.ctrlKey) {
                        // Option+Delete (Mac) or Ctrl+Delete (Windows): delete word right
                        deleteToIndex = this.#getWordEndIndex(selStart);
                    } else {
                        // Regular delete: delete one character
                        deleteToIndex = selStart + 1;
                    }

                    const deletedLength = deleteToIndex - selStart;
                    this.#updateRangesOnTextChange(-deletedLength, selStart);

                    const before = this.textAsset.text.slice(0, selStart);
                    const after = this.textAsset.text.slice(deleteToIndex);
                    const newText = before + after;

                    // If text becomes empty, show empty state
                    if (newText.trim().length === 0) {
                        this.#showEmptyState();
                        this.#selectionStart = 0;
                        this.#selectionEnd = 0;
                    } else {
                        this.textAsset.text = newText;
                    }

                    this.#updateRendererSelection();
                    this.textAsset.requiresRedraw = true;
                    // Deleting text can never cause overflow
                    this.inOverflowState = false;
                }
                this.#resetCaretBlink();
                break;

            case 'ArrowLeft':
                e.preventDefault();
                {
                    let newPos: number;
                    if (e.metaKey) {
                        // Cmd+Left: move to visual left edge of line
                        newPos = this.#isRTL
                            ? this.#getLineEndIndex(this.#selectionEnd)
                            : this.#getLineStartIndex(this.#selectionEnd);
                    } else if (e.altKey || e.ctrlKey) {
                        // Option+Left (Mac) or Ctrl+Left (Win): move by word visually left
                        newPos = this.#isRTL
                            ? this.#getWordEndIndex(this.#selectionEnd)
                            : this.#getWordStartIndex(this.#selectionEnd);
                    } else if (!e.shiftKey && hasSelection) {
                        // No modifier with selection: collapse to visual left end
                        newPos = this.#isRTL ? selEnd : selStart;
                    } else {
                        // Regular: move one character visually left
                        newPos = this.#isRTL
                            ? Math.min(this.#selectionEnd + 1, this.textAsset.text.length)
                            : Math.max(this.#selectionEnd - 1, 0);
                    }

                    if (e.shiftKey) {
                        this.#selectionEnd = newPos;
                    } else {
                        this.#selectionStart = newPos;
                        this.#selectionEnd = newPos;
                    }
                }
                this.#updateRendererSelection();
                this.#resetCaretBlink();
                break;

            case 'ArrowRight':
                e.preventDefault();
                {
                    let newPos: number;
                    if (e.metaKey) {
                        // Cmd+Right: move to visual right edge of line
                        newPos = this.#isRTL
                            ? this.#getLineStartIndex(this.#selectionEnd)
                            : this.#getLineEndIndex(this.#selectionEnd);
                    } else if (e.altKey || e.ctrlKey) {
                        // Option+Right (Mac) or Ctrl+Right (Win): move by word visually right
                        newPos = this.#isRTL
                            ? this.#getWordStartIndex(this.#selectionEnd)
                            : this.#getWordEndIndex(this.#selectionEnd);
                    } else if (!e.shiftKey && hasSelection) {
                        // No modifier with selection: collapse to visual right end
                        newPos = this.#isRTL ? selStart : selEnd;
                    } else {
                        // Regular: move one character visually right
                        newPos = this.#isRTL
                            ? Math.max(this.#selectionEnd - 1, 0)
                            : Math.min(this.#selectionEnd + 1, this.textAsset.text.length);
                    }

                    if (e.shiftKey) {
                        this.#selectionEnd = newPos;
                    } else {
                        this.#selectionStart = newPos;
                        this.#selectionEnd = newPos;
                    }
                }
                this.#updateRendererSelection();
                this.#resetCaretBlink();
                break;

            case 'ArrowUp':
                e.preventDefault();
                {
                    const newPos = this.#getCaretPositionOnAdjacentLine(-1);
                    if (e.shiftKey) {
                        this.#selectionEnd = newPos;
                    } else {
                        this.#selectionStart = newPos;
                        this.#selectionEnd = newPos;
                    }
                    this.#updateRendererSelection();
                    this.#resetCaretBlink();
                }
                break;

            case 'ArrowDown':
                e.preventDefault();
                {
                    const newPos = this.#getCaretPositionOnAdjacentLine(1);
                    if (e.shiftKey) {
                        this.#selectionEnd = newPos;
                    } else {
                        this.#selectionStart = newPos;
                        this.#selectionEnd = newPos;
                    }
                    this.#updateRendererSelection();
                    this.#resetCaretBlink();
                }
                break;

            case 'Home':
                e.preventDefault();
                {
                    let newPos: number;
                    if (e.metaKey || e.ctrlKey) {
                        // Cmd+Home or Ctrl+Home: move to start of document
                        newPos = 0;
                    } else {
                        // Home: move to start of line
                        newPos = this.#getLineStartIndex(this.#selectionEnd);
                    }

                    if (e.shiftKey) {
                        this.#selectionEnd = newPos;
                    } else {
                        this.#selectionStart = newPos;
                        this.#selectionEnd = newPos;
                    }
                }
                this.#updateRendererSelection();
                this.#resetCaretBlink();
                break;

            case 'End':
                e.preventDefault();
                {
                    let newPos: number;
                    if (e.metaKey || e.ctrlKey) {
                        // Cmd+End or Ctrl+End: move to end of document
                        newPos = this.textAsset.text.length;
                    } else {
                        // End: move to end of line
                        newPos = this.#getLineEndIndex(this.#selectionEnd);
                    }

                    if (e.shiftKey) {
                        this.#selectionEnd = newPos;
                    } else {
                        this.#selectionStart = newPos;
                        this.#selectionEnd = newPos;
                    }
                }
                this.#updateRendererSelection();
                this.#resetCaretBlink();
                break;

            case 'Enter':
                e.preventDefault();
                {
                    // In empty state, the actual text is '' (the 'I' is just a placeholder for bounds)
                    const originalText = this.#editingEmptyState ? '' : this.textAsset.text;

                    // Insert newline
                    const before = originalText.slice(0, selStart);
                    let after = originalText.slice(selEnd);

                    // Edge case: if user adds a trailing newline and there's not already one,
                    // insert an additional one so the empty line has height in the metrics.
                    // This matches the behavior of the DOM-based TextEditingManager.
                    if (after === '' && !before.endsWith('\n')) {
                        after = '\n';
                    }

                    const newText = before + '\n' + after;

                    const { canFit, newTextSize } = this.handleOverflowState(newText);
                    if (canFit) {
                        // Remove empty state before making changes
                        if (this.#editingEmptyState) {
                            this.#removeEmptyState();
                        }

                        this.#saveState();
                        const netCharDiff = newText.length - originalText.length;
                        // Update text before ranges to ensure _checkAndFixProperties sees the new text length
                        this.textAsset.text = newText;
                        if (newTextSize !== null) {
                            this.textAsset.textSize = newTextSize;
                        }
                        this.#updateRangesOnTextChange(netCharDiff, selStart);
                        this.#selectionStart = selStart + 1;
                        this.#selectionEnd = selStart + 1;
                        this.#updateRendererSelection();
                        this.textAsset.requiresRedraw = true;
                    }
                }
                this.#resetCaretBlink();
                break;

            default:
                // Handle Ctrl key shortcuts.
                // Emacs-style shortcuts (Ctrl+A/E/F/B/K/H/D/P/N/T) are macOS-only
                // because these keys have standard meanings on Windows/Linux:
                //   Ctrl+A = Select All, Ctrl+F = Find, Ctrl+B = Bold, etc.
                // All Emacs shortcuts are gated to macOS for consistency.
                if (e.ctrlKey && !e.metaKey && CanvasTextEditingManager.#isMac) {
                    const code = e.code?.toLowerCase() || '';
                    const key = e.key?.toLowerCase() || '';

                    // Ctrl+A: Move to start of visual line
                    if (code === 'keya' || key === 'a') {
                        e.preventDefault();
                        e.stopPropagation();
                        const newPos = this.#getLineStartIndex(this.#selectionEnd);
                        if (e.shiftKey) {
                            this.#selectionEnd = newPos;
                        } else {
                            this.#selectionStart = newPos;
                            this.#selectionEnd = newPos;
                        }
                        this.#updateRendererSelection();
                        this.#resetCaretBlink();
                        return;
                    }

                    // Ctrl+E: Move to end of visual line
                    if (code === 'keye' || key === 'e') {
                        e.preventDefault();
                        e.stopPropagation();
                        const newPos = this.#getLineEndIndex(this.#selectionEnd);
                        if (e.shiftKey) {
                            this.#selectionEnd = newPos;
                        } else {
                            this.#selectionStart = newPos;
                            this.#selectionEnd = newPos;
                        }
                        this.#updateRendererSelection();
                        this.#resetCaretBlink();
                        return;
                    }

                    // Ctrl+F: Move one character forward
                    if (code === 'keyf' || key === 'f') {
                        e.preventDefault();
                        e.stopPropagation();
                        let newPos: number;
                        if (!e.shiftKey && hasSelection) {
                            newPos = this.#isRTL ? selStart : selEnd;
                        } else {
                            newPos = this.#isRTL
                                ? Math.max(this.#selectionEnd - 1, 0)
                                : Math.min(this.#selectionEnd + 1, this.textAsset.text.length);
                        }
                        if (e.shiftKey) {
                            this.#selectionEnd = newPos;
                        } else {
                            this.#selectionStart = newPos;
                            this.#selectionEnd = newPos;
                        }
                        this.#updateRendererSelection();
                        this.#resetCaretBlink();
                        return;
                    }

                    // Ctrl+B: Move one character backward
                    if (code === 'keyb' || key === 'b') {
                        e.preventDefault();
                        e.stopPropagation();
                        let newPos: number;
                        if (!e.shiftKey && hasSelection) {
                            newPos = this.#isRTL ? selEnd : selStart;
                        } else {
                            newPos = this.#isRTL
                                ? Math.min(this.#selectionEnd + 1, this.textAsset.text.length)
                                : Math.max(this.#selectionEnd - 1, 0);
                        }
                        if (e.shiftKey) {
                            this.#selectionEnd = newPos;
                        } else {
                            this.#selectionStart = newPos;
                            this.#selectionEnd = newPos;
                        }
                        this.#updateRendererSelection();
                        this.#resetCaretBlink();
                        return;
                    }

                    // Ctrl+K: Delete to end of visual line
                    if (code === 'keyk' || key === 'k') {
                        e.preventDefault();
                        e.stopPropagation();
                        const lineEnd = this.#getLineEndIndex(this.#selectionEnd);
                        if (lineEnd > this.#selectionEnd) {
                            this.#saveState();
                            const deletedLength = lineEnd - this.#selectionEnd;
                            this.#updateRangesOnTextChange(-deletedLength, this.#selectionEnd);
                            const before = this.textAsset.text.slice(0, this.#selectionEnd);
                            const after = this.textAsset.text.slice(lineEnd);
                            this.textAsset.text = before + after;
                            this.#updateRendererSelection();
                            this.textAsset.requiresRedraw = true;
                        }
                        this.#resetCaretBlink();
                        return;
                    }

                    // Ctrl+H: Delete character to the left (like Backspace)
                    if (code === 'keyh' || key === 'h') {
                        e.preventDefault();
                        e.stopPropagation();
                        if (hasSelection) {
                            this.#saveState();
                            this.#deleteSelection();
                        } else if (selStart > 0 && !this.#editingEmptyState) {
                            this.#saveState();
                            const deleteToIndex = selStart - 1;
                            this.#updateRangesOnTextChange(-1, deleteToIndex);
                            const before = this.textAsset.text.slice(0, deleteToIndex);
                            const after = this.textAsset.text.slice(selStart);
                            const newText = before + after;
                            if (newText.trim().length === 0) {
                                this.#showEmptyState();
                                this.#selectionStart = 0;
                                this.#selectionEnd = 0;
                            } else {
                                this.textAsset.text = newText;
                                this.#selectionStart = deleteToIndex;
                                this.#selectionEnd = deleteToIndex;
                            }
                            this.#updateRendererSelection();
                            this.textAsset.requiresRedraw = true;
                            this.inOverflowState = false;
                        }
                        this.#resetCaretBlink();
                        return;
                    }

                    // Ctrl+D: Delete character to the right (like Delete)
                    if (code === 'keyd' || key === 'd') {
                        e.preventDefault();
                        e.stopPropagation();
                        if (hasSelection) {
                            this.#saveState();
                            this.#deleteSelection();
                        } else if (
                            selStart < this.textAsset.text.length &&
                            !this.#editingEmptyState
                        ) {
                            this.#saveState();
                            const deleteToIndex = selStart + 1;
                            this.#updateRangesOnTextChange(-1, selStart);
                            const before = this.textAsset.text.slice(0, selStart);
                            const after = this.textAsset.text.slice(deleteToIndex);
                            const newText = before + after;
                            if (newText.trim().length === 0) {
                                this.#showEmptyState();
                                this.#selectionStart = 0;
                                this.#selectionEnd = 0;
                            } else {
                                this.textAsset.text = newText;
                            }
                            this.#updateRendererSelection();
                            this.textAsset.requiresRedraw = true;
                            this.inOverflowState = false;
                        }
                        this.#resetCaretBlink();
                        return;
                    }

                    // Ctrl+P: Move up one line (like ArrowUp)
                    if (code === 'keyp' || key === 'p') {
                        e.preventDefault();
                        e.stopPropagation();
                        const newPos = this.#getCaretPositionOnAdjacentLine(-1);
                        if (e.shiftKey) {
                            this.#selectionEnd = newPos;
                        } else {
                            this.#selectionStart = newPos;
                            this.#selectionEnd = newPos;
                        }
                        this.#updateRendererSelection();
                        this.#resetCaretBlink();
                        return;
                    }

                    // Ctrl+N: Move down one line (like ArrowDown)
                    if (code === 'keyn' || key === 'n') {
                        e.preventDefault();
                        e.stopPropagation();
                        const newPos = this.#getCaretPositionOnAdjacentLine(1);
                        if (e.shiftKey) {
                            this.#selectionEnd = newPos;
                        } else {
                            this.#selectionStart = newPos;
                            this.#selectionEnd = newPos;
                        }
                        this.#updateRendererSelection();
                        this.#resetCaretBlink();
                        return;
                    }

                    // Ctrl+T: Transpose characters around cursor
                    if (code === 'keyt' || key === 't') {
                        e.preventDefault();
                        e.stopPropagation();
                        if (this.#editingEmptyState || hasSelection) {
                            return;
                        }
                        const text = this.textAsset.text;
                        if (selStart === 0 || text.length < 2) {
                            return;
                        }
                        this.#saveState();
                        let swapPos: number;
                        if (selStart >= text.length) {
                            swapPos = selStart - 1;
                        } else {
                            swapPos = selStart;
                        }
                        const chars = text.split('');
                        const tmp = chars[swapPos - 1];
                        chars[swapPos - 1] = chars[swapPos];
                        chars[swapPos] = tmp;
                        this.textAsset.text = chars.join('');
                        const newPos = Math.min(swapPos + 1, text.length);
                        this.#selectionStart = newPos;
                        this.#selectionEnd = newPos;
                        this.#updateRendererSelection();
                        this.textAsset.requiresRedraw = true;
                        this.#resetCaretBlink();
                        return;
                    }
                }

                // Handle Cmd key shortcuts (standard macOS shortcuts)
                if (e.metaKey && !e.ctrlKey) {
                    switch (e.key.toLowerCase()) {
                        case 'a':
                            // Cmd+A: Select all
                            e.preventDefault();
                            if (this.#editingEmptyState) {
                                return;
                            }
                            this.#selectionStart = 0;
                            this.#selectionEnd = this.textAsset.text.length;
                            this.#updateRendererSelection();
                            return;
                        case 'z':
                            e.preventDefault();
                            if (e.shiftKey) {
                                this.#redo();
                            } else {
                                this.#undo();
                            }
                            return;
                        case 'y':
                            e.preventDefault();
                            this.#redo();
                            return;
                        case 'b':
                        case 'i':
                        case 'u':
                            if (!e.shiftKey && !e.altKey) {
                                e.preventDefault();
                                if (this.isShowingEmptyState) {
                                    return;
                                }
                                const formatType =
                                    e.key.toLowerCase() === 'b'
                                        ? 'bold'
                                        : e.key.toLowerCase() === 'i'
                                          ? 'italic'
                                          : 'underline';
                                this.#toggleFormatting(formatType);
                            }
                            return;
                    }
                }

                // Handle Ctrl key shortcuts for Windows/Linux (Ctrl+A, Ctrl+Z, Ctrl+Y, etc.)
                if (e.ctrlKey && !e.metaKey) {
                    switch (e.key.toLowerCase()) {
                        case 'a':
                            // Ctrl+A: Select all (Windows/Linux standard)
                            e.preventDefault();
                            if (this.#editingEmptyState) {
                                return;
                            }
                            this.#selectionStart = 0;
                            this.#selectionEnd = this.textAsset.text.length;
                            this.#updateRendererSelection();
                            return;
                        case 'z':
                            e.preventDefault();
                            if (e.shiftKey) {
                                this.#redo();
                            } else {
                                this.#undo();
                            }
                            return;
                        case 'y':
                            e.preventDefault();
                            this.#redo();
                            return;
                        case 'b':
                        case 'i':
                        case 'u':
                            if (!e.shiftKey && !e.altKey) {
                                e.preventDefault();
                                if (this.isShowingEmptyState) {
                                    return;
                                }
                                const formatType =
                                    e.key.toLowerCase() === 'b'
                                        ? 'bold'
                                        : e.key.toLowerCase() === 'i'
                                          ? 'italic'
                                          : 'underline';
                                this.#toggleFormatting(formatType);
                            }
                            return;
                    }
                }

                // Handle single printable character input
                // This handles direct keyboard input, which is needed for tests and programmatic events.
                // IME/composition input is handled by the input event handler when isComposing is true.
                //
                // Only handle first keypress, not repeats. This allows:
                // - macOS accent popup to work (holds key, selects accented char)
                // - Key repeat is handled by input events from the browser
                if (e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey && !e.repeat) {
                    // Check if we're in accent popup mode (user holding key for accent selection)
                    // Only block NUMBER keys since that's how accent popup selection works.
                    // Blocking all keys would cause issues with fast typing.
                    if (this.#waitingForAccentPopupSelection && /^[0-9]$/.test(e.key)) {
                        e.preventDefault();
                        return;
                    }

                    // If user types a different character, clear accent popup state
                    // This allows fast typing without being blocked
                    if (this.#lastKeydownKey && this.#lastKeydownKey !== e.key) {
                        this.#lastKeydownKey = null;
                        this.#lastKeydownInsertedChar = null;
                        this.#waitingForAccentPopupSelection = false;
                    }

                    e.preventDefault();
                    this.#lastKeydownKey = e.key;
                    this.#lastKeydownInsertedChar = e.key;
                    this.#insertText(e.key);
                }
                break;
        }
    };

    /**
     * Inserts text at the current cursor position, replacing any selection.
     * Used for character input and composition text.
     */
    #insertText(char: string) {
        const sanitizedChar = sanitizeText(char);
        if (sanitizedChar.length === 0) {
            return;
        }

        const selStart = Math.min(this.#selectionStart, this.#selectionEnd);
        const selEnd = Math.max(this.#selectionStart, this.#selectionEnd);

        // In empty state, the actual text is '' (the 'I' is just a placeholder for bounds)
        const originalText = this.#editingEmptyState ? '' : this.textAsset.text;
        const beforeSelection = originalText.slice(0, selStart);
        const afterSelection = originalText.slice(selEnd);
        const updatedText = beforeSelection + sanitizedChar + afterSelection;

        const { canFit, newTextSize } = this.handleOverflowState(updatedText);
        if (!canFit) {
            return;
        }

        // Remove empty state before making changes
        if (this.#editingEmptyState) {
            this.#removeEmptyState();
        }

        this.#saveState();
        this.inOverflowState = false;

        const deletedLength = selEnd - selStart;
        const netCharDiff = sanitizedChar.length - deletedLength;

        // If text becomes only whitespace, show empty state instead
        if (updatedText.trim().length === 0) {
            this.#showEmptyState();
            this.#selectionStart = 0;
            this.#selectionEnd = 0;
            this.#updateRendererSelection();
            this.#resetCaretBlink();
            this.textAsset.requiresRedraw = true;
            return;
        }

        // Update text before ranges to ensure _checkAndFixProperties sees the new text length
        this.textAsset.text = updatedText;
        if (newTextSize !== null) {
            this.textAsset.textSize = newTextSize;
        }
        this.#updateRangesOnTextChange(netCharDiff, selStart);

        const newCursorPos = selStart + sanitizedChar.length;
        this.#selectionStart = newCursorPos;
        this.#selectionEnd = newCursorPos;

        this.#updateRendererSelection();
        this.#resetCaretBlink();
        this.textAsset.requiresRedraw = true;
    }

    #deleteSelection() {
        const selStart = Math.min(this.#selectionStart, this.#selectionEnd);
        const selEnd = Math.max(this.#selectionStart, this.#selectionEnd);

        const deletedLength = selEnd - selStart;
        this.#updateRangesOnTextChange(-deletedLength, selStart);

        const before = this.textAsset.text.slice(0, selStart);
        const after = this.textAsset.text.slice(selEnd);
        const newText = before + after;

        // If text becomes empty (or only whitespace), show empty state
        if (newText.trim().length === 0) {
            this.#showEmptyState();
            this.#selectionStart = 0;
            this.#selectionEnd = 0;
        } else {
            this.textAsset.text = newText;
            this.#selectionStart = selStart;
            this.#selectionEnd = selStart;
        }

        this.#updateRendererSelection();
        this.textAsset.requiresRedraw = true;
        // Deleting text can never cause overflow, so reset overflow state
        this.inOverflowState = false;
    }

    /**
     * Updates highlight and formatting ranges when text is modified.
     * @param netCharDiff - The net change in characters (positive for additions, negative for deletions)
     * @param changeStartIdx - The index where the change started
     */
    #updateRangesOnTextChange(netCharDiff: number, changeStartIdx: number) {
        if (netCharDiff === 0) {
            return;
        }

        if (this.textAsset.textHighlights.length) {
            const originalHighlights = this.textAsset.textHighlights.map((h) => h.slice());
            this.textAsset.textHighlights = getUpdatedHighlightRanges(
                netCharDiff,
                changeStartIdx,
                originalHighlights,
            ) as [number, number][];
        }

        if (this.textAsset.textBold.length) {
            const originalBold = this.textAsset.textBold.map((r) => r.slice());
            this.textAsset.textBold = getUpdatedHighlightRanges(
                netCharDiff,
                changeStartIdx,
                originalBold,
            ) as [number, number][];
        }

        if (this.textAsset.textItalic.length) {
            const originalItalic = this.textAsset.textItalic.map((r) => r.slice());
            this.textAsset.textItalic = getUpdatedHighlightRanges(
                netCharDiff,
                changeStartIdx,
                originalItalic,
            ) as [number, number][];
        }

        if (this.textAsset.textUnderline.length) {
            const originalUnderline = this.textAsset.textUnderline.map((r) => r.slice());
            this.textAsset.textUnderline = getUpdatedHighlightRanges(
                netCharDiff,
                changeStartIdx,
                originalUnderline,
            ) as [number, number][];
        }
    }

    #updateRendererSelection() {
        this.textAsset.renderer.setEditingSelection(this.#selectionStart, this.#selectionEnd);
        this.textAsset.requiresRedraw = true;

        // Update the visible container bounds to match text asset
        this.#updateRootContainerBounds();

        // Sync DOM selection only when there is an actual range (not a collapsed cursor).
        // This is needed so Cmd+C / context menu Copy work correctly.
        // Collapsed cursors don't need sync — there's nothing to copy.
        // During pointer drag, sync is deferred to pointerup.
        if (!this.#isPointerDown && this.#selectionStart !== this.#selectionEnd) {
            this.#syncDomSelection();
        }

        // Notify web layer of selection change so it can update UI (e.g., bold/italic button state).
        // Native selectionchange fires asynchronously and not reliably for programmatic changes,
        // so we dispatch it synchronously on the document to ensure the toolbar updates immediately.
        document.dispatchEvent(new Event('selectionchange'));
    }

    /**
     * Synchronizes the DOM selection with the canvas selection.
     * This allows window.getSelection() to return the correct selection state,
     * which is required by the web layer for toolbar operations.
     */
    #syncDomSelection() {
        if (!this.#hiddenInput) {
            return;
        }

        // Suppress MutationObserver while we update the DOM ourselves
        this.#ignoreDomMutations = true;
        try {
            // Only update textContent when it actually changed to avoid destroying
            // the text node (and any existing DOM selection) unnecessarily.
            const currentText = this.textAsset.text;
            if (this.#hiddenInput.textContent !== currentText) {
                this.#hiddenInput.textContent = currentText;
            }

            // Set DOM selection to match canvas selection
            const selection = window.getSelection();
            if (!selection) {
                return;
            }

            const textNode = this.#hiddenInput.firstChild;
            if (!textNode) {
                return;
            }

            const start = Math.min(this.#selectionStart, this.#selectionEnd);
            const end = Math.max(this.#selectionStart, this.#selectionEnd);

            // Clamp to valid range
            const textLength = currentText.length;
            const clampedStart = Math.max(0, Math.min(start, textLength));
            const clampedEnd = Math.max(0, Math.min(end, textLength));

            selection.setBaseAndExtent(textNode, clampedStart, textNode, clampedEnd);
        } catch (_) {
            // Ignore errors from invalid selection ranges
        } finally {
            this.#ignoreDomMutations = false;
        }
    }

    #caretCurrentlyVisible = true;
    // Track character inserted by keydown for coordination with input events
    #lastKeydownInsertedChar: string | null = null;
    // Track the key that was pressed (cleared on keyup) - used to detect accent popup
    #lastKeydownKey: string | null = null;
    // Track if browser confirmed the keydown with an input event (accent popup mode)
    #waitingForAccentPopupSelection = false;

    #startCaretBlink() {
        this.#stopCaretBlink();
        this.#caretCurrentlyVisible = true;
        this.textAsset.renderer.setCaretVisible(true);

        this.#caretBlinkInterval = setInterval(() => {
            const selection = this.textAsset.renderer.getEditingSelection();
            if (selection && selection[0] === selection[1]) {
                // Only blink when caret is visible (no selection)
                this.#caretCurrentlyVisible = !this.#caretCurrentlyVisible;
                this.textAsset.renderer.setCaretVisible(this.#caretCurrentlyVisible);
                this.textAsset.requiresRedraw = true;
            }
        }, 530);
    }

    #stopCaretBlink() {
        if (this.#caretBlinkInterval) {
            clearInterval(this.#caretBlinkInterval);
            this.#caretBlinkInterval = null;
        }
    }

    #resetCaretBlink() {
        this.textAsset.renderer.setCaretVisible(true);
        this.#startCaretBlink();
    }

    /**
     * Updates the caret color to match the text fill color (same as DOM implementation).
     */
    updateCaretColor() {
        const metrics = this.textAsset.renderer.metrics;
        if (!metrics) return;

        // Use the fill color from the normal style, converted to RGB string
        const fillColor = this.textAsset.normalStyle.fill;
        if (typeof fillColor === 'number') {
            const caretColor = int2rgb(fillColor);
            this.textAsset.renderer.setCaretColor(caretColor);
        }
    }

    /**
     * Handles overflow state checking for a given text value.
     * Returns true if text can fit (with optional resizing), false otherwise.
     * If text can fit with a different font size, returns the new font size.
     */
    handleOverflowState(text: string): { canFit: boolean; newTextSize: number | null } {
        // Empty text always fits
        if (text.length === 0) {
            this.inOverflowState = false;
            return { canFit: true, newTextSize: null };
        }

        // When in empty state, textAsset.text is already set to placeholder 'I'
        // which provides proper bounds for canFitText calculation
        let canTextFit = this.textAsset.canFitText(text);

        let siblingsCanFitText = true;
        let newTextSize: number | null = null;

        // slight change to existing textSize to make sure and use it for max value
        // to make sure getFontSizeToFitAllText returns different size for textSize
        const MIN_SIZE_SHIFT = 5;
        const minSize = this.textAsset._textEditingOptions?.minSize ?? 10;

        if (
            !canTextFit &&
            this.textAsset.textSize > minSize &&
            this.textAsset.textSize - MIN_SIZE_SHIFT >= minSize
        ) {
            const { fontSize, displaysAllText } = this.textAsset.getFontSizeToFitAllText(text, {
                max: this.textAsset.textSize - MIN_SIZE_SHIFT,
                min: minSize,
            });
            if (displaysAllText) {
                newTextSize = fontSize;
            }
            canTextFit = displaysAllText;
        }

        // Ensure other text assets in the group can fit text too,
        // since max height is shared among text assets in the same group
        const group = this.textAsset.parent?.parent;
        if (group && Config.isAsset(group) && Config.isAssetGroup(group)) {
            for (const asset of group.getAssetsByType(Config.TEXT_ASSET_TYPE)) {
                if (asset === this.textAsset) {
                    continue;
                }
                siblingsCanFitText = asset.canFitText(asset.text);
                const siblingMinSize = asset._textEditingOptions?.minSize ?? 10;
                if (
                    !siblingsCanFitText &&
                    asset.textSize > siblingMinSize &&
                    asset.textSize - MIN_SIZE_SHIFT >= siblingMinSize
                ) {
                    const { fontSize, displaysAllText } = asset.getFontSizeToFitAllText(
                        asset.text,
                        {
                            max: asset.textSize - MIN_SIZE_SHIFT,
                            min: siblingMinSize,
                        },
                    );
                    if (displaysAllText) {
                        asset.textSize = fontSize;
                    }
                    siblingsCanFitText = displaysAllText;
                }
                if (!siblingsCanFitText) {
                    break;
                }
            }
        }

        this.inOverflowState = !canTextFit || !siblingsCanFitText;
        return { canFit: canTextFit && siblingsCanFitText, newTextSize };
    }

    /**
     * Sets cursor position from canvas coordinates.
     */
    calculateCursorPos(x: number, y: number): { charIndex: number } {
        const charIndex = this.#getCharacterIndexAtPosition(x, y);
        return { charIndex };
    }

    /**
     * Converts scene coordinates (reference resolution) to output resolution coordinates,
     * which is the coordinate system expected by #getCharacterIndexAtPosition.
     */
    #convertSceneCoordsToMetricsCoords(sceneX: number, sceneY: number): { x: number; y: number } {
        const root = this.textAsset.root;
        const refWidth = root.referenceResolution.width;
        const refHeight = root.referenceResolution.height;
        const outputWidth = root.outputResolution.width;
        const outputHeight = root.outputResolution.height;

        // Convert reference resolution to output resolution
        const canvasX = sceneX * (outputWidth / refWidth);
        const canvasY = sceneY * (outputHeight / refHeight);

        return { x: canvasX, y: canvasY };
    }

    /**
     * Sets the caret position from reference resolution scene coordinates.
     * Call this after enableTextInput to position the caret at the click location.
     */
    setCaretAtPosition(x: number, y: number) {
        const { x: convertedX, y: convertedY } = this.#convertSceneCoordsToMetricsCoords(x, y);
        const charIndex = this.#getCharacterIndexAtPosition(convertedX, convertedY);
        this.#selectionStart = charIndex;
        this.#selectionEnd = charIndex;
        this.#updateRendererSelection();
        this.#resetCaretBlink();
        this.focus();
    }

    /**
     * Sets the selection range programmatically.
     */
    setSelection(start: number, end: number) {
        this.#selectionStart = Math.max(0, Math.min(start, this.textAsset.text.length));
        this.#selectionEnd = Math.max(0, Math.min(end, this.textAsset.text.length));
        this.#updateRendererSelection();
        this.#resetCaretBlink();
    }

    /**
     * Gets the current selection range.
     */
    getSelection(): [number, number] {
        return [
            Math.min(this.#selectionStart, this.#selectionEnd),
            Math.max(this.#selectionStart, this.#selectionEnd),
        ];
    }

    /**
     * Toggles text formatting (bold, italic, or underline) for the current selection.
     * If the selection is already fully formatted, removes the formatting.
     * Otherwise, applies the formatting to the selection.
     */
    #toggleFormatting(formatType: 'bold' | 'italic' | 'underline') {
        const [selStart, selEnd] = this.getSelection();
        if (selStart === selEnd) {
            return;
        }

        const propertyName = `text${formatType.charAt(0).toUpperCase() + formatType.slice(1)}` as
            | 'textBold'
            | 'textItalic'
            | 'textUnderline';
        const currentRanges: [number, number][] = this.textAsset[propertyName];

        const isFullyFormatted = this.#isRangeFullyFormatted(selStart, selEnd, currentRanges);

        let newRanges: [number, number][];
        if (isFullyFormatted) {
            newRanges = this.#removeFormattingFromRange(selStart, selEnd, currentRanges);
        } else {
            newRanges = this.#addFormattingToRange(selStart, selEnd, currentRanges);
        }

        this.textAsset[propertyName] = newRanges;
        this.textAsset.requiresRedraw = true;
    }

    /**
     * Checks if a range is fully covered by the existing formatting ranges
     */
    #isRangeFullyFormatted(start: number, end: number, ranges: [number, number][]): boolean {
        let cursor = start;
        const sortedRanges = [...ranges].sort((a, b) => a[0] - b[0]);

        for (const [rangeStart, rangeEnd] of sortedRanges) {
            if (rangeStart <= cursor && rangeEnd >= cursor) {
                cursor = rangeEnd;
                if (cursor >= end) {
                    return true;
                }
            }
        }
        return false;
    }

    /**
     * Removes formatting from a range, splitting existing ranges if necessary
     */
    #removeFormattingFromRange(
        start: number,
        end: number,
        ranges: [number, number][],
    ): [number, number][] {
        const newRanges: [number, number][] = [];

        for (const [rangeStart, rangeEnd] of ranges) {
            if (rangeEnd <= start || rangeStart >= end) {
                newRanges.push([rangeStart, rangeEnd]);
            } else {
                if (rangeStart < start) {
                    newRanges.push([rangeStart, start]);
                }
                if (rangeEnd > end) {
                    newRanges.push([end, rangeEnd]);
                }
            }
        }

        return newRanges;
    }

    /**
     * Adds formatting to a range, merging with adjacent/overlapping ranges
     */
    #addFormattingToRange(
        start: number,
        end: number,
        ranges: [number, number][],
    ): [number, number][] {
        let newStart = start;
        let newEnd = end;
        const nonOverlapping: [number, number][] = [];

        for (const [rangeStart, rangeEnd] of ranges) {
            if (rangeEnd < start || rangeStart > end) {
                nonOverlapping.push([rangeStart, rangeEnd]);
            } else {
                newStart = Math.min(newStart, rangeStart);
                newEnd = Math.max(newEnd, rangeEnd);
            }
        }

        nonOverlapping.push([newStart, newEnd]);
        return nonOverlapping.sort((a, b) => a[0] - b[0]);
    }

    /**
     * Performs cleanup when the manager is no longer needed.
     */
    destroy() {
        this.#destroyed = true;
        this.#domMutationObserver?.disconnect();
        this.#domMutationObserver = null;
        this.#stopCaretBlink();
        this.#removeEventListeners();

        // Clear any pending state
        this.#lastKeydownInsertedChar = null;
        this.#lastKeydownKey = null;
        this.#waitingForAccentPopupSelection = false;

        // If in empty state, clear the placeholder text
        if (this.#editingEmptyState) {
            this.textAsset.text = '';
            this.textAsset.renderer.setHideTextContent(false);
            this.#editingEmptyState = false;
        }

        if (this.#nteContainer && this.#nteContainer.parentNode) {
            this.#nteContainer.parentNode.removeChild(this.#nteContainer);
        }
        this.#nteContainer = null;
        this.#hiddenInput = null;

        if (this.#selectionStyleElement && this.#selectionStyleElement.parentNode) {
            this.#selectionStyleElement.parentNode.removeChild(this.#selectionStyleElement);
        }
        this.#selectionStyleElement = null;

        if (this.#selectionColorDetector && this.#selectionColorDetector.parentNode) {
            this.#selectionColorDetector.parentNode.removeChild(this.#selectionColorDetector);
        }
        this.#selectionColorDetector = null;

        // Clear selection and outline in renderer
        this.textAsset.renderer.setEditingSelection(null, null);
        this.textAsset.renderer.setEditingOutlineColor(null);
        this.textAsset.requiresRedraw = true;
    }

    /**
     * Focus the hidden input to enable keyboard capture.
     */
    focus() {
        this.#hiddenInput?.focus({ preventScroll: true });
    }
}
